/**
 * Create Opportunity Modal
 *
 * Modal for creating a new sales opportunity
 */

import { useState, useEffect, useRef } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { Plus, Building2, Calendar, DollarSign, ChevronsUpDown, Check, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { getAccounts } from '@/features/accounts/api';
import { OPPORTUNITY_TYPES, PRIORITIES, VALIDATION } from '../../constants';
import type { CreateOpportunityInput, OpportunityType, OpportunityPriority } from '../../types';

const PAGE_SIZE = 15;

interface CreateOpportunityModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (input: CreateOpportunityInput) => Promise<void>;
  isLoading?: boolean;
}

export function CreateOpportunityModal({
  open,
  onOpenChange,
  onConfirm,
  isLoading,
}: CreateOpportunityModalProps) {
  const [accountSearch, setAccountSearch] = useState('');
  const [accountOpen, setAccountOpen] = useState(false);
  const [selectedAccountLabel, setSelectedAccountLabel] = useState('');
  const [pageNumber, setPageNumber] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState<CreateOpportunityInput>({
    account_id: '',
    title: '',
    type: 'new_business',
    description: '',
    estimated_value: undefined,
    target_close_date: '',
    priority: 'medium',
  });

  // Debounce account search to avoid firing on every keystroke
  const debouncedAccountSearch = useDebounce(accountSearch, 300);

  // Reset page when search changes
  useEffect(() => {
    setPageNumber(0);
  }, [debouncedAccountSearch]);

  // Fetch accounts for selection (server-side search + pagination)
  const { data: accountsData, isLoading: isLoadingAccounts, isFetching } = useQuery({
    queryKey: ['accounts', { search: debouncedAccountSearch, pageNumber, pageLength: PAGE_SIZE }],
    queryFn: () =>
      getAccounts({
        search: debouncedAccountSearch || undefined,
        pageNumber,
        pageLength: PAGE_SIZE,
      }),
    enabled: open,
  });

  const accounts = accountsData?.accounts || [];
  const total = accountsData?.total || 0;
  const hasMore = (pageNumber + 1) * PAGE_SIZE < total;

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setFormData({
        account_id: '',
        title: '',
        type: 'new_business',
        description: '',
        estimated_value: undefined,
        target_close_date: '',
        priority: 'medium',
      });
      setAccountSearch('');
      setSelectedAccountLabel('');
      setPageNumber(0);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!formData.account_id || !formData.title) return;
    await onConfirm(formData);
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleSelectAccount = (accountId: string, accountName: string, accountNumber: string) => {
    setFormData((prev) => ({ ...prev, account_id: accountId }));
    setSelectedAccountLabel(`${accountName} (${accountNumber})`);
    setAccountOpen(false);
  };

  const isFormValid = formData.account_id && formData.title && formData.title.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-primary" />
            Create New Opportunity
          </DialogTitle>
          <DialogDescription>
            Create a new sales opportunity to track through the pipeline. All opportunities start in
            the "Prospect Acquired" stage.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
          {/* Account Selection - Searchable Combobox */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1">
              <Building2 className="h-3.5 w-3.5" />
              Account <span className="text-red-500">*</span>
            </Label>
            <Popover open={accountOpen} onOpenChange={setAccountOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={accountOpen}
                  className={cn(
                    'w-full justify-between font-normal',
                    !formData.account_id && 'text-muted-foreground'
                  )}
                  disabled={isLoading}
                >
                  {selectedAccountLabel || 'Search by name or account number...'}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Search accounts..."
                    value={accountSearch}
                    onValueChange={(value) => setAccountSearch(value)}
                  />
                  <CommandList ref={listRef}>
                    {isLoadingAccounts && accounts.length === 0 ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        <span className="ml-2 text-sm text-muted-foreground">Loading accounts...</span>
                      </div>
                    ) : accounts.length === 0 ? (
                      <CommandEmpty>
                        {debouncedAccountSearch
                          ? `No accounts matching "${debouncedAccountSearch}"`
                          : 'No accounts found'}
                      </CommandEmpty>
                    ) : (
                      <CommandGroup>
                        {accounts.map((account) => (
                          <CommandItem
                            key={account.id}
                            value={account.id}
                            onSelect={() =>
                              handleSelectAccount(account.id, account.name, account.account_number)
                            }
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                formData.account_id === account.id ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium truncate">{account.name}</div>
                              <div className="text-xs text-muted-foreground">{account.account_number}</div>
                            </div>
                          </CommandItem>
                        ))}

                        {/* Pagination */}
                        {(hasMore || pageNumber > 0) && (
                          <div className="flex items-center justify-between border-t px-2 py-2 mt-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs"
                              disabled={pageNumber <= 0 || isFetching}
                              onClick={(e) => {
                                e.preventDefault();
                                setPageNumber((p) => Math.max(0, p - 1));
                              }}
                            >
                              Previous
                            </Button>
                            <span className="text-xs text-muted-foreground">
                              {isFetching ? (
                                <Loader2 className="h-3 w-3 animate-spin inline" />
                              ) : (
                                `${pageNumber * PAGE_SIZE + 1}-${Math.min((pageNumber + 1) * PAGE_SIZE, total)} of ${total}`
                              )}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs"
                              disabled={!hasMore || isFetching}
                              onClick={(e) => {
                                e.preventDefault();
                                setPageNumber((p) => p + 1);
                              }}
                            >
                              Next
                            </Button>
                          </div>
                        )}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">
              Title <span className="text-red-500">*</span>
            </Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Q1 Expansion - 50 new sensors"
              maxLength={VALIDATION.OPPORTUNITY.TITLE_MAX_LENGTH}
              disabled={isLoading}
            />
            <p className="text-xs text-gray-500">
              {formData.title.length}/{VALIDATION.OPPORTUNITY.TITLE_MAX_LENGTH} characters
            </p>
          </div>

          {/* Type */}
          <div className="space-y-2">
            <Label htmlFor="type">Opportunity Type</Label>
            <Select
              value={formData.type}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, type: value as OpportunityType }))
              }
              disabled={isLoading}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OPPORTUNITY_TYPES.map((type) => (
                  <SelectItem key={type.key} value={type.key}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Estimated Value */}
          <div className="space-y-2">
            <Label htmlFor="value" className="flex items-center gap-1">
              <DollarSign className="h-3.5 w-3.5" />
              Estimated Value (CAD)
            </Label>
            <Input
              id="value"
              type="number"
              min={VALIDATION.OPPORTUNITY.ESTIMATED_VALUE_MIN}
              max={VALIDATION.OPPORTUNITY.ESTIMATED_VALUE_MAX}
              value={formData.estimated_value ?? ''}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  estimated_value: e.target.value ? Number(e.target.value) : undefined,
                }))
              }
              placeholder="e.g., 50000"
              disabled={isLoading}
            />
          </div>

          {/* Target Close Date */}
          <div className="space-y-2">
            <Label htmlFor="close-date" className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              Target Close Date
            </Label>
            <Input
              id="close-date"
              type="date"
              value={formData.target_close_date}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, target_close_date: e.target.value }))
              }
              disabled={isLoading}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <Label htmlFor="priority">Priority</Label>
            <Select
              value={formData.priority}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, priority: value as OpportunityPriority }))
              }
              disabled={isLoading}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((priority) => (
                  <SelectItem key={priority.key} value={priority.key}>
                    {priority.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Add any additional details about this opportunity..."
              maxLength={VALIDATION.OPPORTUNITY.DESCRIPTION_MAX_LENGTH}
              rows={3}
              disabled={isLoading}
            />
            <p className="text-xs text-gray-500">
              {formData.description?.length || 0}/{VALIDATION.OPPORTUNITY.DESCRIPTION_MAX_LENGTH}{' '}
              characters
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!isFormValid || isLoading}>
            {isLoading ? 'Creating...' : 'Create Opportunity'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
